import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAccount } from '@azure/msal-react';
import { nanoid } from 'nanoid';
import { Checkbox, Button } from '@upmc/ims.component-library';
import { addMessage, clearMessages } from '../../store/ancillary/actions';
import { Redirect } from 'react-router-dom';
import axiosInstance from '../../services/axios/axios';
import fileDownload from 'js-file-download';
import { WrapImsPacket } from '../../store/utility';
import FileUpload from '../../components/UI/Input/FileUpload/FileUpload';
import { DataTable } from '../../components/UI/MDC/DataTable/DataTable';
import { ACTION_TYPES } from '../../components/UI/MDC/DataTable/_types';
import Card, { Actions } from '../../components/UI/MDC/Card/Card';
import {
	Headline4,
	Headline6,
	Caption,
	Subtitle2,
} from '../../components/UI/MDC/Typography/Typography';
import List, {
	ListItem,
	MDCListItemText,
} from '../../components/UI/MDC/List/List';
import Icon from '../../components/UI/MDC/Icon/Icon';
import WithLoadingHandler from '../../hoc/withLoadingHandler/withLoadingHandler';
import { RequestTypes } from '../../constants/enums';
import { CSS_CLASSES } from './_constants';

import classes from './BulkServiceAccountTool.module.scss';
import StyleAuxiliary from '../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

const BulkServiceAccountCreationTool = props => {
	const [sAccountTypeSelection, setAccountTypeSelection] = useState(); // Key,Value object corresponding to account type ID, value.
	const [sAreOwnersValid, setAreOwnersValid] = useState(false);
	const [sArrAccountType, setArrAccountType] = useState([]);
	const [sArrAdditionalOptionSelections, setArrAdditionalOptionSelections] =
		useState([]);
	const [sArrApplications, setArrApplications] = useState([]); // array of Key,Value objects corresponding to application ID, name.
	const [sArrApplicationSelections, setArrApplicationSelections] = useState(
		[],
	);
	const [sArrOwners, setArrOwners] = useState([]); // array of objects including base property "Data"
	const [sIsDevTeam, setIsDevTeam] = useState(false);
	const [sIsExpectedData, setIsExpectedData] = useState(true);
	const [sIsLoading, setIsLoading] = useState(false); // boolean for validation loading animation
	const [sWasThresholdMet, setWasThresholdMet] = useState(true);
	const [sWasThresholdExceeded, setWasThresholdExceeded] = useState(false);
	const [sIsSubmitting, setIsSubmitting] = useState(false); // boolean for submission loading animation
	const [sJustification, setJustification] = useState();
	const [sRequestTrackingNumber, setRequestTrackingNumber] = useState();
	const [sUserNetworkID, setUserNetworkID] = useState();
	const [sWasSubmitted, setWasSubmitted] = useState(false); // boolean for tracking validation
	const [sOpenModal, setOpenModal] = useState(false);

	const dispatch = useDispatch();
	const account = useAccount();

	const VALIDATION_THRESHOLD_MIN = 3;
	const VALIDATION_THRESHOLD_MAX = 200;
	const additionalOptions = [
		{
			value: 'Suppress Email',
			hoverText:
				'Suppress all related email communications to end users.',
		},
	]; // array of objects including base properties "value" and "hoverText"
	const rowHeaders = [
		'Owner Network ID',
		'Owner Familiar ESO ID',
		'Owner Email',
		'Service Account Network ID',
	];

	/**
	 * Effect on component mount. Queries the current user's information and performs initial page population accordingly.
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		const regex = new RegExp(/^.*(?=@)/);
		const arrUser = account?.username ? regex.exec(account.username) : null;
		const user = arrUser ? arrUser[0] : null;
		setUserNetworkID(user);
		axiosInstance
			.post(
				'api/BulkServiceAccountCreationTool/Populate',
				WrapImsPacket(user),
			)
			.then(response => {
				const payload = response.data.payload;
				let arrAccountType = payload.AccountTypes
					? Object.keys(payload.AccountTypes)
							.map(key => {
								return {
									key: payload.AccountTypes[key].Key,
									value: payload.AccountTypes[key].Value,
								};
							})
							.sort((firstEl, secondEl) => {
								const valueFirstEl =
									firstEl.value.toLowerCase();
								const valueSecondEl =
									secondEl.value.toLowerCase();
								if (valueFirstEl < valueSecondEl) {
									return -1;
								}
								if (valueFirstEl > valueSecondEl) {
									return 1;
								}
								return 0;
							})
					: [];
				setArrAccountType(arrAccountType);
				setIsDevTeam(payload.DevTeamMember);
				if (payload.DevTeamMember) {
					setArrAdditionalOptionSelections(['Suppress Email']);
				}
			})
			.catch(error => {
				raiseErrorBanner(error);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Effect on local state change. Resets validation on state change.
	 * Does not need to check value of local state, value of local state considered when rendering validation message.
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		if (sWasSubmitted) {
			setWasSubmitted(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sAccountTypeSelection, sArrApplicationSelections, sArrOwners]);

	/**
	 * Account type change handler. Retrieves application(s) associated with selected account type.
	 * @param {Object} accountTypeSelection Nullable Key,Value pair for account type selection.
	 * @returns void.
	 */
	const onAccountTypeChangeHandler = accountTypeSelection => {
		setAccountTypeSelection(accountTypeSelection);
		setArrApplicationSelections([]);
		setArrOwners([]);
		axiosInstance
			.post(
				'api/BulkServiceAccountCreationTool/Applications',
				WrapImsPacket(
					accountTypeSelection ? accountTypeSelection.Key : '',
				),
			)
			.then(response => {
				setArrApplications(response.data.payload);
			})
			.catch(error => {
				raiseErrorBanner(error);
			});
	};

	/**
	 * Event handler for data table cell input elements. Updates state object according to user input.
	 * @param {Event} e event raised by user change.
	 * @param {Object} owner changed row in state object.
	 * @param {String} key key of changed column in state object.
	 * @returns void
	 */
	const onCellChangedHandler = (e, owner, key) => {
		let arrOwnersCopy = [...sArrOwners];
		const ownerIndex = arrOwnersCopy.indexOf(owner);
		arrOwnersCopy[ownerIndex].Cells[key].Value = e.target.value;
		setArrOwners(arrOwnersCopy);
	};

	/**
	 * Maintains an array of unique item selections. Pushes to (or) splices from array depending on existence.
	 * @param {Object} itemSelected object equivalent of selected item.
	 * @param {Array} itemSelections array of existing unique item selection objects.
	 * @returns updated array of unique item selections.
	 * @example
	 * // returns [{foo: true}, {foo: false}]
	 * onCheckBoxSelect({foo: false}, [{foo: true}])
	 * // returns [{foo: true}]
	 * onCheckBoxSelect({foo: false}, [{foo: true}, {foo: false}])
	 */
	const onCheckBoxSelect = (itemSelected, itemSelections) => {
		let itemSelectionsCopy = [...itemSelections];

		if (itemSelections.includes(itemSelected)) {
			itemSelectionsCopy.splice(itemSelections.indexOf(itemSelected), 1);
		} else {
			itemSelectionsCopy.push(itemSelected);
		}
		return itemSelectionsCopy;
	};

	/**
	 * Retrieves template data defined on server and downloads to file in browser.
	 * @external fileDownload
	 * @see {@link https://github.com/kennethjiang/js-file-download|js-file-download}
	 * @returns void
	 */
	const onDownloadHandler = () => {
		axiosInstance
			.get('api/BulkServiceAccountCreationTool/Template')
			.then(response => {
				fileDownload(
					response.data,
					'BulkServiceAccountCreationTool_Template.csv',
				);
			})
			.catch(error => {
				raiseErrorBanner(error);
			});
	};

	/**
	 * Event handler raised when file successfully uploaded. Stores file content in state if content passes client-side validation.
	 * @param {Object} payload custom object containing the following properties of uploaded file: "fileName", "fileHeaders", "fileContent"
	 * @returns void
	 */
	const onFileUploadHandler = async payload => {
		const wasThresholdMet =
			payload.fileContent.length >= VALIDATION_THRESHOLD_MIN;
		const wasThresholdExceeded =
			payload.fileContent.length > VALIDATION_THRESHOLD_MAX;
		let isExpectedData = true;
		if (wasThresholdMet && !wasThresholdExceeded) {
			const fileHeaders = payload.fileHeaders.map(fileHeader =>
				fileHeader.replace(/_/g, ' ').trim(),
			);
			isExpectedData =
				rowHeaders.length === fileHeaders.length &&
				fileHeaders.every(
					fileHeader => fileHeader && rowHeaders.includes(fileHeader),
				);
			if (isExpectedData) {
				const owners = payload.fileContent.map(row => {
					const validation = {
						Key: String(true),
						Value: {},
					};
					const arrRow = row.split(',');

					let rowObject = {
						ActionCells: [
							{
								BindTo: 0,
								Checked: false,
								Key: nanoid(),
							},
						],
						Key: nanoid(),
						Cells: {
							ESO_FI_ID: {
								Value: null,
								Validation: validation,
							},
							Name: {
								Value: '',
								Validation: validation,
							},
						},
					};
					rowHeaders.map(header => {
						let validationCopy = { ...validation };
						validationCopy.Key = String(false);

						const index = fileHeaders.findIndex(
							fileHeader => header === fileHeader,
						);

						rowObject.Cells[payload.fileHeaders[index]] = {
							Value:
								arrRow.length >= index
									? arrRow[index].trim()
									: null,
							Validation: validationCopy,
						};
						return null;
					});
					return rowObject;
				});
				try {
					await onValidateHandler(owners);
				} catch (error) {
					raiseErrorBanner(error);
				}
			}
		}
		setWasThresholdMet(wasThresholdMet);
		setWasThresholdExceeded(wasThresholdExceeded);
		setIsExpectedData(isExpectedData);
	};

	/**
	 * Event handler for data table row removals. Removes row from state object and recalculates validity of updated state object.
	 * @param {Object} owner removed row in state object.
	 * @returns void
	 */
	const onRowRemovedHandler = owner => {
		let arrOwnersCopy = [...sArrOwners];
		const ownerIndex = arrOwnersCopy.indexOf(owner);
		arrOwnersCopy.splice(ownerIndex, 1);
		setArrOwners(arrOwnersCopy);
		setAreOwnersValid(
			arrOwnersCopy.length > 0
				? !arrOwnersCopy.some(
						owner =>
							Object.keys(owner.Cells).findIndex(
								key =>
									owner.Cells[
										key
									].Validation.Key.toLowerCase() === 'false',
							) > -1,
					)
				: false,
		);
	};

	/**
	 * Submission click handler. Constructs payload for server-side processing if form valid.
	 * @returns void
	 */
	const onSubmissionHandler = async () => {
		if (sArrOwners && sArrOwners.length > 0) {
			let isValid = sAreOwnersValid;
			if (!isValid) {
				try {
					isValid = await onValidateHandler(sArrOwners);
				} catch (error) {
					raiseErrorBanner(error);
				}
			}
			if (
				(sArrApplicationSelections.length > 0 ||
					props.actionType === 'Revoke') &&
				isValid &&
				!sIsLoading
			) {
				setIsSubmitting(true);

				if (props.actionType === 'Add') {
					axiosInstance
						.post(
							'/api/BulkServiceAccountCreationTool/Submission',
							WrapImsPacket({
								LoggedOnUser: sUserNetworkID,
								DevTeamMember: sIsDevTeam,
								AdditionalOptions:
									sArrAdditionalOptionSelections,
								AccountType: sAccountTypeSelection,
								Justification: sJustification,
								Applications: sArrApplicationSelections.map(
									app => {
										return {
											ID: app.Key,
											Name: app.Value,
										};
									},
								),
								Owners: sArrOwners,
								RequestType: 'Bulk',
							}),
						)
						.then(response => {
							setRequestTrackingNumber(
								Object.keys(
									response.data.payload.WIPHeaderDetails,
								)[0],
							);
						})
						.catch(error => {
							raiseErrorBanner(error);
							setIsSubmitting(false);
						});
				} else {
					axiosInstance
						.post(
							'/api/BulkServiceAccountCreationTool/RevokeSubmission',
							WrapImsPacket({
								LoggedOnUser: sUserNetworkID,
								DevTeamMember: sIsDevTeam,
								AdditionalOptions:
									sArrAdditionalOptionSelections,
								Justification: sJustification,
								Owners: sArrOwners,
								RequestType: 'Bulk',
							}),
						)
						.then(response => {
							setRequestTrackingNumber(
								Object.keys(
									response.data.payload.WIPHeaderDetails,
								)[0],
							);
						})
						.catch(error => {
							raiseErrorBanner(error);
							setIsSubmitting(false);
						});
				}
			}
		}
		setWasSubmitted(true);
	};

	/**
	 * Constructs payload for server-side validation. Payload includes account type and processed owner data.
	 * @param {Array} owners array of owner data.
	 * @returns {Promise<boolean>} indicating validity of payload.
	 */
	const onValidateHandler = async (owners = null) => {
		setIsLoading(true);

		let promise = new Promise((resolve, reject) => {
			if (props.actionType === 'Add') {
				axiosInstance
					.post(
						'api/BulkServiceAccountCreationTool',
						WrapImsPacket({
							Owners: {
								AccountType: sAccountTypeSelection.Key,
								OwnerList: owners,
							},
							Applications: sArrApplicationSelections.map(app => {
								return {
									ID: app.Key,
									Name: app.Value,
								};
							}),
							RequestType: 'Bulk',
						}),
					)
					.then(response => {
						setArrOwners(response.data.payload.OwnerList);
						setAreOwnersValid(response.data.payload.AreValid);
						resolve(response.data.payload.AreValid);
					})
					.catch(error => {
						reject(error);
					});
			} else {
				axiosInstance
					.post(
						'api/BulkServiceAccountCreationToolRevoke',
						WrapImsPacket({
							Owners: {
								OwnerList: owners,
							},
						}),
					)
					.then(response => {
						setArrOwners(response.data.payload.OwnerList);
						setAreOwnersValid(response.data.payload.AreValid);
						resolve(response.data.payload.AreValid);
					})
					.catch(error => {
						reject(error);
					});
			}
		});
		promise.catch(error => error).finally(() => setIsLoading(false));
		return promise;
	};

	/**
	 * Method for dispatching action to raise banner with error message.
	 * @param {Object} error error raised.
	 * @returns void.
	 */
	const raiseErrorBanner = error => {
		let message =
			error && error.response && error.response.data
				? error.response.data.payload
				: error.message;
		dispatch(clearMessages());
		dispatch(
			addMessage({
				Key: 3,
				Value: message,
			}),
		);
	};

	let justificationInput = (sAccountTypeSelection ||
		props.actionType === 'Revoke') && (
		<Fragment>
			<Headline6>Justification</Headline6>
			<input
				type='text'
				className={CSS_CLASSES.CONTROL}
				value={sJustification}
				onChange={e => setJustification(e.target.value)}
			/>
		</Fragment>
	);

	let applicationsList = sAccountTypeSelection &&
		sArrApplications.length > 0 && (
			<Fragment>
				<Headline6>Applications</Headline6>
				{sArrApplicationSelections.length < 1 && sWasSubmitted ? (
					<Subtitle2 className={classes[CSS_CLASSES.INVALID]}>
						Please select at least one application.
					</Subtitle2>
				) : null}
				<List
					checkboxList
					className={classes[CSS_CLASSES.CHECKBOX_CONTAINER]}
					selectedIndex={sArrApplicationSelections.map(app =>
						sArrApplications.indexOf(app),
					)}
					handleSelect={indexSelected => {
						setArrApplicationSelections(
							onCheckBoxSelect(
								sArrApplications[indexSelected],
								sArrApplicationSelections,
							),
						);
						setArrOwners([]);
					}}
					xlarge
				>
					{sArrApplications.map(app => {
						const checked =
							sArrApplicationSelections.indexOf(app) > -1;
						return (
							<ListItem key={app.Key}>
								<Checkbox checked={checked} />
								<MDCListItemText primaryText={app.Value} />
							</ListItem>
						);
					})}
				</List>
			</Fragment>
		);
	let additionalOptionsList = sIsDevTeam && (
		<Fragment>
			<Headline6>Additional Options</Headline6>
			<List
				checkboxList
				selectedIndex={sArrAdditionalOptionSelections.map(option =>
					additionalOptions.indexOf(option.value),
				)}
				handleSelect={indexSelected => {
					setArrAdditionalOptionSelections(
						onCheckBoxSelect(
							additionalOptions[indexSelected].value,
							sArrAdditionalOptionSelections,
						),
					);
				}}
				xlarge
			>
				{additionalOptions.map((option, index) => {
					const checked =
						sArrAdditionalOptionSelections.indexOf(option.value) >
						-1;
					return (
						<ListItem key={index}>
							<Checkbox checked={checked} label={option.value} />
							{option.hoverText ? (
								<Icon
									className={
										classes[CSS_CLASSES.CHECKBOX_ICON]
									}
									icon='info'
									title={option.hoverText}
									aria-label={option.hoverText}
								/>
							) : null}
						</ListItem>
					);
				})}
			</List>
		</Fragment>
	);
	let fileUploadValidationMessage = !sIsExpectedData
		? 'Unexpected data. Please ensure uploaded file matches template file.'
		: !sWasThresholdMet
			? 'A minimum of ' +
				VALIDATION_THRESHOLD_MIN +
				' users is supported. Please add users.'
			: sWasThresholdExceeded
				? 'A maximum of ' +
					VALIDATION_THRESHOLD_MAX +
					' users is supported. Please remove users.'
				: (!sArrOwners || sArrOwners.length < 1) &&
					  sWasSubmitted &&
					  !sWasThresholdExceeded
					? 'Please upload a file, no users uploaded.'
					: null;
	let fileUploadContainer = ((sAccountTypeSelection &&
		sArrApplicationSelections.length > 0) ||
		props.actionType === 'Revoke') && (
		<Fragment>
			<FileUpload
				disabled={sIsLoading}
				fileTypes={'.xls,.txt,.csv'}
				fileMIMETypes={'application/vnd.ms-excel,text/csv,text/plain'}
				onClickHandler={payload => onFileUploadHandler(payload)}
				parseRegex={new RegExp(/(\r?\n)/)}
				resetSelection={sArrOwners ? !(sArrOwners.length > 0) : false}
			/>
			{fileUploadValidationMessage ? (
				<Subtitle2 className={classes[CSS_CLASSES.INVALID]}>
					{fileUploadValidationMessage}
				</Subtitle2>
			) : null}
		</Fragment>
	);

	let ownersDataTable = sArrOwners &&
		sArrOwners.length > 0 &&
		!sIsLoading && (
			<DataTable
				actions={[
					{
						onChangedHandler: (_, __, row) =>
							onRowRemovedHandler(row),
						Type: ACTION_TYPES.REMOVE,
					},
				]}
				headers={[
					{
						Value: 'Name',
					},
					...rowHeaders.map(header => {
						return { Value: header };
					}),
				]}
				label={'Users (' + sArrOwners.length + ')'}
				rows={sArrOwners}
				validation={{
					minimumThreshold: 3,
					maximumThreshold: 200,
					message:
						!sAreOwnersValid && sWasSubmitted
							? 'Please resolve all invalid users.'
							: null,
					onCellChangedHandler: (e, row, key) =>
						onCellChangedHandler(e, row, key),
				}}
			/>
		);

	let modal = sOpenModal && (
		<Dialog open={sOpenModal} onClose={() => setOpenModal(false)}>
			<DialogTitle>Service Account Revocation</DialogTitle>
			<DialogContent>
				All application access associated with the provided UPMC Service
				Account(s) will be terminated.
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setOpenModal(false)} variant='text'>
					Cancel
				</Button>
				<Button
					onClick={() => {
						setOpenModal(false);
						onSubmissionHandler();
					}}
					variant='text'
				>
					Proceed
				</Button>
			</DialogActions>
		</Dialog>
	);

	let submissionForm = !sIsSubmitting && props.actionType === 'Add' && (
		<Fragment>
			<Headline6>Account Type</Headline6>
			{!sAccountTypeSelection && sWasSubmitted ? (
				<Subtitle2 className={classes[CSS_CLASSES.INVALID]}>
					Please select an account type.
				</Subtitle2>
			) : null}
			<select
				className={CSS_CLASSES.CONTROL}
				value={sAccountTypeSelection ? sAccountTypeSelection.Key : ''}
				onChange={e => {
					onAccountTypeChangeHandler(
						e.target.value
							? {
									Key: e.target.value,
									Value: e.target.selectedOptions[0].text,
								}
							: null,
					);
				}}
			>
				<option key='0' value=''>
					--Select--
				</option>
				{sArrAccountType.map((accountType, index) => (
					<option key={index++} value={accountType.key}>
						{accountType.value}
					</option>
				))}
			</select>
			{justificationInput}
			{applicationsList}
			{additionalOptionsList}
			{fileUploadContainer}
			<WithLoadingHandler isLoading={sIsLoading}>
				{ownersDataTable}
			</WithLoadingHandler>
			<Actions className={classes[CSS_CLASSES.ACTIONS]}>
				<Button
					variant='contained'
					onClick={onSubmissionHandler}
					disabled={sIsLoading}
				>
					Submit
				</Button>
			</Actions>
		</Fragment>
	);

	let revokeForm = !sIsSubmitting && props.actionType === 'Revoke' && (
		<Fragment>
			{modal}
			{justificationInput}
			{additionalOptionsList}
			{fileUploadContainer}
			<WithLoadingHandler isLoading={sIsLoading}>
				{ownersDataTable}
			</WithLoadingHandler>
			<Actions className={classes[CSS_CLASSES.ACTIONS]}>
				<Button
					variant='contained'
					onClick={() => {
						sAreOwnersValid
							? setOpenModal(true)
							: onSubmissionHandler();
					}}
					disabled={sIsLoading}
				>
					Submit
				</Button>
			</Actions>
		</Fragment>
	);

	return sRequestTrackingNumber ? (
		<div>
			{props.actionType === 'Add' ? (
				<Redirect
					to={
						'/RRS/Request_ModifyDetail_View.aspx?tn=' +
						sRequestTrackingNumber +
						'&REQUEST_TYPE=' +
						RequestTypes.MULTI_SERVICE_ACCOUNT
					}
				/>
			) : (
				<Redirect to={'/Confirm'} />
			)}
		</div>
	) : (
		<StyleAuxiliary classes={classes[CSS_CLASSES.CONTAINER]}>
			<Card large className={classes[CSS_CLASSES.CARD]}>
				<div>
					<div>
						<StyleAuxiliary
							classes={classes[CSS_CLASSES.TITLE_CONTAINER]}
						>
							<Button className={classes[CSS_CLASSES.HIDDEN]}>
								Help
							</Button>
							<div>
								{props.actionType === 'Add' ? (
									<Headline4
										className={classes[CSS_CLASSES.TITLE]}
									>
										Bulk Service Account Creation Tool
									</Headline4>
								) : (
									<Headline4
										className={classes[CSS_CLASSES.TITLE]}
									>
										Bulk Service Account Creation Tool
										(Revoke)
									</Headline4>
								)}
							</div>
							<Button
								variant='text'
								aria-label='Help'
								color='primary'
								href='https://infonet.upmc.com/infonet/ls/content/5780739462973863/bulk-service-account-creation-pdf-1764799963'
								rel='noopener noreferrer'
								target='_blank'
								title='Help'
							>
								Help
							</Button>
						</StyleAuxiliary>
						<Card
							xlarge
							className={classes[CSS_CLASSES.TITLE_BODY]}
						>
							<Caption>
								<ol
									className={
										classes[CSS_CLASSES.TITLE_BODY_LIST]
									}
								>
									<div>
										{props.actionType === 'Add' ? (
											<div>
												<li
													className={
														classes[
															CSS_CLASSES
																.TITLE_BODY_LIST_NO_INDENT
														]
													}
												>
													To submit batch requests of{' '}
													{VALIDATION_THRESHOLD_MIN}-
													{VALIDATION_THRESHOLD_MAX}{' '}
													users for service account
													creation:
												</li>
												<li>
													Select <b>Account Type</b>{' '}
													and associated{' '}
													<b>Applications</b> as well
													as any desired{' '}
													<b>Additional Options</b>.
												</li>
											</div>
										) : (
											<div>
												<li
													className={
														classes[
															CSS_CLASSES
																.TITLE_BODY_LIST_NO_INDENT
														]
													}
												>
													To submit batch requests of{' '}
													{VALIDATION_THRESHOLD_MIN}-
													{VALIDATION_THRESHOLD_MAX}{' '}
													users for service account
													termination:
												</li>
												<li>
													Select any desired{' '}
													<b>Additional Options</b>.
												</li>
											</div>
										)}
									</div>
									<li>
										<b>
											<Button
												variant='text'
												sx={{
													p: '0',
													fontSize: 'inherit',
												}}
												onClick={onDownloadHandler}
											>
												Download this spreadsheet
												template
											</Button>{' '}
										</b>
										and save it to OneDrive.
									</li>
									<div>
										{props.actionType === 'Add' ? (
											<li>
												Open the template and enter the
												new account information. Save
												the template as a '.csv' or
												'.txt' file and exit Excel.
											</li>
										) : (
											<li>
												Open the template and enter the
												account information for the
												service accounts you would like
												to revoke access to. Save the
												template as a '.csv' or '.txt'
												file and exit Excel.
											</li>
										)}
									</div>
									<li>
										Select <b>Upload From File</b> to upload
										this populated template file.
									</li>
									<li>
										If validation unsuccessful, remove users
										or perform in-line changes and
										re-attempt submission until successful.
									</li>
								</ol>
							</Caption>
						</Card>
						<WithLoadingHandler isLoading={sIsSubmitting}>
							{submissionForm}
							{revokeForm}
						</WithLoadingHandler>
					</div>
				</div>
			</Card>
		</StyleAuxiliary>
	);
};

BulkServiceAccountCreationTool.propTypes = {
	actionType: PropTypes.string,
};

export default BulkServiceAccountCreationTool;
